import {
  composeRenderProps,
  type ModalOverlayProps,
  Modal as RACModal,
  ModalOverlay as RACModalOverlay,
} from "react-aria-components";
import { tv, type VariantProps } from "tailwind-variants";

export const overlayStyles = tv({
  base: [
    "fixed left-0 top-0 isolate z-101 h-(--visual-viewport-height) w-full bg-black/15 dark:bg-black/40",
    "flex text-center justify-center items-center",
    "[&:has([role=dialog])]:items-end [&:has([role=dialog])]:pt-4 [&:has([role=dialog])]:[--visual-viewport-vertical-padding:16px]",
    "sm:[&:has([role=dialog])]:items-center sm:[&:has([role=dialog])]:p-4 sm:[&:has([role=dialog])]:[--visual-viewport-vertical-padding:32px]",

    /**
     * Style for stack dialogs
     */
    // First dialog
    "[&:has(~[data-ui=modal-overlay]:not([data-exiting]))>[data-ui=modal]>section]:opacity-75",
    "[&:has(~[data-ui=modal-overlay]:not([data-exiting]))>[data-ui=modal]]:bg-background",

    "[&:has(~[data-ui=modal-overlay])>[data-ui=modal]]:transform-[scale,y]",
    "[&:has(~[data-ui=modal-overlay])>[data-ui=modal]]:ease-cool",
    "[&:has(~[data-ui=modal-overlay])>[data-ui=modal]]:duration-150",

    // When the nested dialog is not closing
    "[&:has(~[data-ui=modal-overlay]:not([data-exiting]))>[data-ui=modal]]:scale-90",
    "[&:has(~[data-ui=modal-overlay]:not([data-exiting]))>[data-ui=modal]]:translate-y-4",

    // Remove nested dialog overlay background and fade in effect
    "[&:has(~[data-ui=modal-overlay])~[data-ui=modal-overlay]]:bg-transparent",
    "[&:has(~[data-ui=modal-overlay])~[data-ui=modal-overlay]]:fade-in-100",

    // Make both dialogs close immediately
    "[&:has(~[data-ui=modal-overlay])~[data-ui=modal-overlay][data-exiting]]:opacity-0",
    "[&[data-exiting]:has(~[data-ui=modal-overlay])]:opacity-0",
  ],
  variants: {
    isEntering: {
      true: ["animate-in fade-in duration-150 ease-cool"],
    },
    isExiting: {
      true: ["animate-out fade-out duration-150 ease-cool"],
    },
  },
});

export const modalStyles = tv({
  base: [
    "relative max-h-full w-full text-left align-middle shadow-lg bg-background border rounded-lg",
    "has-[[role=dialog]]:rounded-t-lg has-[[role=dialog]]:rounded-b-none sm:has-[[role=dialog]]:rounded-lg",
  ],
  variants: {
    size: {
      default:
        "sm:has-[[role=alertdialog]]:max-w-md sm:has-[[role=dialog]]:max-w-lg",
      xs: "sm:max-w-xs",
      sm: "sm:max-w-sm",
      md: "sm:max-w-md",
      lg: "sm:max-w-lg",
      xl: "sm:max-w-lg",
      "2xl": "sm:max-w-2xl",
      "3xl": "sm:max-w-3xl",
      "4xl": "sm:max-w-4xl",
      "5xl": "sm:max-w-5xl",
      fullWidth: "w-full",
    },
    isEntering: {
      true: [
        "animate-in zoom-in-95 ease-cool duration-150",
        "has-[[role=dialog]]:zoom-in-100 has-[[role=dialog]]:slide-in-from-bottom sm:has-[[role=dialog]]:zoom-in-95 sm:has-[[role=dialog]]:slide-in-from-bottom-0",
      ],
    },
    isExiting: {
      true: [
        "animate-out zoom-out-95 ease-cool duration-150",
        "has-[[role=dialog]]:zoom-out-100 has-[[role=dialog]]:slide-out-to-bottom sm:has-[[role=dialog]]:zoom-out-95 sm:has-[[role=dialog]]:slide-out-to-bottom-0",
      ],
    },
  },
  defaultVariants: {
    size: "default",
  },
});

type ModalVariants = VariantProps<typeof modalStyles>;

type ModalProps = Omit<ModalOverlayProps, "className"> & {
  ref?: React.RefObject<HTMLDivElement | null>;
  classNames?: {
    modalOverlay?: ModalOverlayProps["className"];
    modal?: ModalOverlayProps["className"];
  };
  size?: ModalVariants["size"];
};

export function Modal({
  classNames,
  ref,
  isDismissable = true,
  size,
  isOpen,
  onOpenChange,
  ...props
}: ModalProps) {
  return (
    <RACModalOverlay
      {...props}
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      isDismissable={isDismissable}
      data-ui="modal-overlay"
      className={composeRenderProps(
        classNames?.modalOverlay,
        (className, renderProps) =>
          overlayStyles({ ...renderProps, className }),
      )}
    >
      <RACModal
        {...props}
        isDismissable={isDismissable}
        ref={ref}
        data-ui="modal"
        className={composeRenderProps(
          classNames?.modal,
          (className, renderProps) =>
            modalStyles({ ...renderProps, size, className }),
        )}
      />
    </RACModalOverlay>
  );
}
